import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { UserAvatar } from "@/components/ui/UserAvatar";
import { UserName } from "@/components/badges/UserName";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  HelpCircle,
  Plus,
  CheckCircle,
  MessageSquare,
  ArrowLeft,
  Check,
} from "lucide-react";
import {
  useCourseQuestions,
  useCourseQuestion,
  useQuestionAnswers,
  useCreateQuestion,
  useCreateAnswer,
  useToggleQuestionResolved,
  useToggleAnswerAccepted,
  CourseQuestion,
} from "@/hooks/useCourseQuestions";
import { useAuth } from "@/lib/auth";
import { useHasRole } from "@/hooks/useUserRoles";
import { formatDistanceToNow } from "date-fns";
import { toast } from "sonner";

interface QABoardSectionProps {
  courseId: string;
  courseInstructorId?: string;
}

export function QABoardSection({
  courseId,
  courseInstructorId,
}: QABoardSectionProps) {
  const [selectedQuestion, setSelectedQuestion] = useState<string | null>(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  if (selectedQuestion) {
    return (
      <QuestionThread
        questionId={selectedQuestion}
        courseInstructorId={courseInstructorId}
        onBack={() => setSelectedQuestion(null)}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <HelpCircle className="h-5 w-5" />
          Q&A Board
        </h3>
        <Button onClick={() => setShowCreateDialog(true)} size="sm">
          <Plus className="h-4 w-4 mr-2" />
          Ask Question
        </Button>
      </div>

      <QuestionList
        courseId={courseId}
        onSelectQuestion={setSelectedQuestion}
      />

      <CreateQuestionDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        courseId={courseId}
      />
    </div>
  );
}

function QuestionList({
  courseId,
  onSelectQuestion,
}: {
  courseId: string;
  onSelectQuestion: (id: string) => void;
}) {
  const { data: questions, isLoading } = useCourseQuestions(courseId);

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (!questions || questions.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-12 text-center">
          <HelpCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h3 className="font-medium mb-2">No questions yet</h3>
          <p className="text-sm text-muted-foreground">
            Be the first to ask a question about this course
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {questions.map((question) => (
        <QuestionCard
          key={question.id}
          question={question}
          onClick={() => onSelectQuestion(question.id)}
        />
      ))}
    </div>
  );
}

function QuestionCard({
  question,
  onClick,
}: {
  question: CourseQuestion;
  onClick: () => void;
}) {
  return (
    <Card
      className="cursor-pointer hover:border-primary/50 transition-colors"
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex gap-3">
          <UserAvatar avatarPath={question.author?.avatar_url} fullName={question.author?.full_name} className="h-10 w-10 flex-shrink-0" fallbackClassName="bg-primary text-primary-foreground" thumbnailSize={64} />

          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="flex items-center gap-2">
                {question.is_resolved && (
                  <CheckCircle className="h-4 w-4 text-green-500" />
                )}
                <h4 className="font-medium truncate">{question.title}</h4>
              </div>
              <Badge variant="secondary" className="flex-shrink-0">
                <MessageSquare className="h-3 w-3 mr-1" />
                {question.answer_count || 0}
              </Badge>
            </div>

            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
              {question.content}
            </p>

            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <span><UserName userId={question.user_id} name={question.author?.full_name} /></span>
              <span>•</span>
              <span>
                {formatDistanceToNow(new Date(question.created_at), {
                  addSuffix: true,
                })}
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function QuestionThread({
  questionId,
  courseInstructorId,
  onBack,
}: {
  questionId: string;
  courseInstructorId?: string;
  onBack: () => void;
}) {
  const { user } = useAuth();
  const isInstructor = useHasRole("instructor");
  const isAdmin = useHasRole("admin");
  const { data: question, isLoading: loadingQuestion } =
    useCourseQuestion(questionId);
  const { data: answers, isLoading: loadingAnswers } =
    useQuestionAnswers(questionId);
  const createAnswer = useCreateAnswer();
  const toggleResolved = useToggleQuestionResolved();
  const toggleAccepted = useToggleAnswerAccepted();
  const [newAnswer, setNewAnswer] = useState("");

  const canModerate =
    isInstructor ||
    isAdmin ||
    user?.id === courseInstructorId ||
    user?.id === question?.user_id;

  const handleSubmitAnswer = async () => {
    if (!newAnswer.trim() || !user?.id) return;

    try {
      await createAnswer.mutateAsync({
        questionId,
        userId: user.id,
        content: newAnswer,
      });
      toast.success("Answer posted");
      setNewAnswer("");
    } catch {
      toast.error("Failed to post answer");
    }
  };

  const handleToggleResolved = async () => {
    if (!question) return;
    try {
      await toggleResolved.mutateAsync({
        questionId: question.id,
        isResolved: !question.is_resolved,
      });
      toast.success(
        question.is_resolved ? "Marked as unresolved" : "Marked as resolved"
      );
    } catch {
      toast.error("Failed to update question");
    }
  };

  if (loadingQuestion) {
    return <Skeleton className="h-64 w-full" />;
  }

  if (!question) {
    return <p>Question not found</p>;
  }

  return (
    <div className="space-y-4">
      <Button variant="ghost" onClick={onBack} className="mb-2">
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Questions
      </Button>

      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              {question.is_resolved && (
                <Badge
                  variant="secondary"
                  className="bg-green-500/10 text-green-600"
                >
                  <CheckCircle className="h-3 w-3 mr-1" />
                  Resolved
                </Badge>
              )}
              <CardTitle className="text-xl">{question.title}</CardTitle>
            </div>
            {canModerate && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleToggleResolved}
              >
                {question.is_resolved
                  ? "Mark Unresolved"
                  : "Mark as Resolved"}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <UserAvatar avatarPath={question.author?.avatar_url} fullName={question.author?.full_name} className="h-10 w-10" thumbnailSize={64} />
            <div>
              <div className="flex items-center gap-2 text-sm">
                <UserName userId={question.user_id} name={question.author?.full_name} className="font-medium" />
                <span className="text-muted-foreground">
                  {formatDistanceToNow(new Date(question.created_at), {
                    addSuffix: true,
                  })}
                </span>
              </div>
              <p className="mt-2 whitespace-pre-wrap">{question.content}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">
            {answers?.length || 0} Answers
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {loadingAnswers ? (
            <Skeleton className="h-32 w-full" />
          ) : answers && answers.length > 0 ? (
            answers.map((answer) => (
              <div
                key={answer.id}
                className={`p-4 rounded-lg border ${
                  answer.is_accepted
                    ? "border-green-500/50 bg-green-500/5"
                    : ""
                }`}
              >
                <div className="flex gap-3">
                  <UserAvatar avatarPath={answer.author?.avatar_url} fullName={answer.author?.full_name} className="h-8 w-8" thumbnailSize={64} />
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm">
                        <UserName userId={answer.user_id} name={answer.author?.full_name} className="font-medium" />
                        <span className="text-muted-foreground">
                          {formatDistanceToNow(new Date(answer.created_at), {
                            addSuffix: true,
                          })}
                        </span>
                        {answer.is_accepted && (
                          <Badge
                            variant="secondary"
                            className="bg-green-500/10 text-green-600"
                          >
                            <Check className="h-3 w-3 mr-1" />
                            Accepted
                          </Badge>
                        )}
                      </div>
                      {canModerate && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            toggleAccepted.mutate({
                              answerId: answer.id,
                              isAccepted: !answer.is_accepted,
                            })
                          }
                        >
                          {answer.is_accepted ? "Unaccept" : "Accept Answer"}
                        </Button>
                      )}
                    </div>
                    <p className="mt-2 whitespace-pre-wrap">{answer.content}</p>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              No answers yet. Be the first to help!
            </p>
          )}

          <div className="pt-4 border-t">
            <h4 className="font-medium mb-3">Your Answer</h4>
            <Textarea
              placeholder="Write your answer..."
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              rows={4}
            />
            <Button
              className="mt-3"
              onClick={handleSubmitAnswer}
              disabled={createAnswer.isPending || !newAnswer.trim()}
            >
              Post Answer
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function CreateQuestionDialog({
  open,
  onOpenChange,
  courseId,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  courseId: string;
}) {
  const { user } = useAuth();
  const createQuestion = useCreateQuestion();
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");

  const handleSubmit = async () => {
    if (!title.trim() || !content.trim() || !user?.id) {
      toast.error("Please fill in all fields");
      return;
    }

    try {
      await createQuestion.mutateAsync({
        courseId,
        userId: user.id,
        title,
        content,
      });
      toast.success("Question posted");
      setTitle("");
      setContent("");
      onOpenChange(false);
    } catch {
      toast.error("Failed to post question");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Ask a Question</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Input
              placeholder="Question title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>
          <div>
            <Textarea
              placeholder="Describe your question in detail..."
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={createQuestion.isPending}>
            Post Question
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
